import { Button } from "@/components/core/buttons/button";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage, FormPrepend } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { RadioGroup, RadioGroupItem } from "@/components/core/forms/radio-group.tsx";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { type CounselFormSchema, counselFormSchema } from "@/features/case-evaluation/types/counsel";
import { useReplaceCounselMutation } from "@/features/case-record/api/queries/case-records-query";
import { usersQueryOptions } from "@/features/dashboard/api/queries/user-queries";
import cityService from "@/features/dashboard/api/services/city-service";
import provinceService from "@/features/dashboard/api/services/province-service";
import regionService from "@/features/dashboard/api/services/region-service";
import userService from "@/features/dashboard/api/services/user-service";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconHelpCircle } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import Alert from "../core/alert";
import { Checkbox } from "../core/forms/checkbox";
import Combobox from "../core/forms/combobox";
import Tooltip from "../core/tooltip";

export default function ReplaceCounselModal({
	data,
	user,
	emails,
	isEdit,
	lawFirm,
	lawFirmEmail,
	lawFirmRegion,
	lawFirmProvince,
	lawFirmCity,
	lawFirmBarangay,
	lawFirmAddress1,
	lawFirmAddress2,
	lawFirmZipcode,
}: {
	data?: CounselFormSchema;
	user?: any;
	emails?: (string | undefined)[];
	isEdit?: boolean;
	lawFirm?: (string | undefined)[];
	lawFirmEmail?: (string | undefined)[];
	lawFirmRegion?: (string | undefined)[];
	lawFirmProvince?: (string | undefined)[];
	lawFirmCity?: (string | undefined)[];
	lawFirmBarangay?: (string | undefined)[];
	lawFirmAddress1?: (string | undefined)[];
	lawFirmAddress2?: (string | undefined)[];
	lawFirmZipcode?: (string | undefined)[];
}) {
	const queryClient = useQueryClient();
	// const usersQuery = useSuspenseQuery(
	// 	usersQueryOptions({
	// 		include: "roles",
	// 		role: "Counsel",
	// 	}),
	// );

	// const users = usersQuery.data.data;

	const { closeModal } = useModalControl();
	const form = useForm<any>({
		defaultValues: {
			replacement_type: "rb1",
			first_name: data?.first_name || "",
			last_name: data?.last_name || "",
			middle_name: data?.middle_name || "",
			email: data?.email || "",
			mobile_number: data?.mobile_number || "",
			landline: data?.landline || "",
			region_code: data?.region_code || "",
			region: data?.region || {},
			province_code: data?.province_code || "",
			province: data?.province || {},
			city_code: data?.city_code || "",
			city: data?.city || {},
			replacement_counsel_id: "",
			replacement_counsel: {},
			barangay: data?.barangay || "",
			zipcode: data?.zipcode || "",
			address_1: data?.address_1 || "",
			address_2: data?.address_2 || "",
			law_firm: data?.law_firm || "",
			law_firm_email: data?.law_firm_email || "",
			lawfirm_region_code: data?.lawfirm_region_code || "",
			lawfirm_province_code: data?.lawfirm_province_code || "",
			lawfirm_city_code: data?.lawfirm_city_code || "",
			lawfirm_barangay: data?.lawfirm_barangay || "",
			lawfirm_address_1: data?.lawfirm_address_1 || "",
			lawfirm_address_2: data?.lawfirm_address_2 || "",
			lawfirm_zipcode: data?.lawfirm_zipcode || "",
			advanced_information: data?.advanced_information || false,
			suffix: data?.suffix || "",
		},
	});
	const [debouncedEmail, setDebouncedEmail] = useState("");
	const [regions, setRegions] = useState([]);
	const [provinces, setProvinces] = useState([]);
	const [cities, setCities] = useState([]);
	const [regionLabel, setRegionLabel] = useState("");
	const [provinceLabel, setProvinceLabel] = useState("");
	const [cityLabel, setCityLabel] = useState("");
	const replaceCounselMutation = useReplaceCounselMutation();

	const [emailExists, setEmailExists] = useState(false);
	const email = form.watch("email");

	const advanced = form.watch("advanced_information");

	const lawfirmRegionCode = form.watch("lawfirm_region_code");
	const lawfirmProvinceCode = form.watch("lawfirm_province_code");
	const replacementType = form.watch("replacement_type");
	const replacementCounsel = form.watch("replacement_counsel");
	const updateDebouncedEmail = useCallback(
		debounce(
			(newEmail) => {
				setDebouncedEmail(newEmail);
			},
			500,
			{ trailing: true },
		),
		[],
	);

	useEffect(() => {
		updateDebouncedEmail(email);
	}, [email, updateDebouncedEmail]);

	useEffect(() => {
		if (debouncedEmail) {
			userService
				.validateEmail(debouncedEmail)
				.then((response) => {
					if (response) {
						const user = response;
						setEmailExists(true);

						for (const key in user) {
							const value = user[key as keyof typeof user];
							form.setValue(key as keyof CounselFormSchema, (value as string) ?? "");
						}
					} else {
						setEmailExists(false);
					}
				})
				.catch((_) => {
					setEmailExists(false);
				});
		}
	}, [debouncedEmail, form]);

	const regionCode = form.watch("region_code");
	const provinceCode = form.watch("province_code");

	const sameEmail = emails?.includes(form.watch("email") || "");
	const isLawfirmSame = lawFirm && form.watch("law_firm") && lawFirm.includes(form.watch("law_firm"));

	useEffect(() => {
		if (isLawfirmSame) {
			console.log(lawFirmEmail, lawFirmRegion);
			form.setValue("law_firm_email", lawFirmEmail);
			form.setValue("lawfirm_region_code", lawFirmRegion);
			form.setValue("lawfirm_province_code", lawFirmProvince);
			form.setValue("lawfirm_city_code", lawFirmCity);
			form.setValue("lawfirm_barangay", lawFirmBarangay);
			form.setValue("lawfirm_address_1", lawFirmAddress1);
			form.setValue("lawfirm_address_2", lawFirmAddress2);
			form.setValue("lawfirm_zipcode", lawFirmZipcode);
		}
	}, [isLawfirmSame]);

	useEffect(() => {
		if (isLawfirmSame && regions.length) {
			if (lawFirmRegion) {
				const regionModel = regions.find((region) => region.code === lawFirmRegion);
				setRegionLabel(regionModel?.name);
			}
		}
	}, [isLawfirmSame, regions]);

	const onSubmit = async (formData) => {
		try {
			await replaceCounselMutation.mutateAsync({
				caseRecordId: user.case_record_id,
				data: {
					user,
					formData,
				},
			});

			closeModal();
		} catch (error) {}
	};

	const undef = form.watch("email") === "" && emails === "";

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full rounded-md border border-border/50">
						<div className="flex flex-col items-center justify-between w-full py-3 px-4 gap-3">
							<FormField
								control={form.control}
								name="replacement_type"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Select replacement type</FormLabel>
										</div>
										<RadioGroup onValueChange={field.onChange} defaultValue={field.value}>
											<FormItem>
												<div className="flex flex-row items-center w-full gap-4">
													<FormControl>
														<RadioGroupItem value="rb1" />
													</FormControl>
													<FormLabel>
														<div className="flex flex-col w-full">
															<span className="text-base text-foreground">New</span>
														</div>
													</FormLabel>

													<FormControl>
														<RadioGroupItem value="rb2" />
													</FormControl>
													<FormLabel>
														<div className="flex flex-col w-full">
															<span className="text-base text-foreground">Existing</span>
														</div>
													</FormLabel>
												</div>
											</FormItem>
										</RadioGroup>
									</FormItem>
								)}
							/>
						</div>

						{replacementType === "rb1" && (
							<div className="flex flex-col items-center justify-between w-full py-3 px-4 gap-3">
								{emailExists && (
									<div className="col-span-12 w-full py-1">
										<Alert type="info" allowClose={false} title="Email Address Already Exists" icon={<IconAlertCircle size={20} stroke={2} />}>
											The system will use the personal information associated with the email address you entered to auto-fill the form fields. If you do not
											wish to use this information, please enter a different email address.
										</Alert>
									</div>
								)}

								<div className="grid grid-cols-12 w-full py-1 gap-x-4 gap-y-3">
									<div className="col-span-12 flex items-center text-[14px] text-gray-500">
										<div className="flex flex-col w-full">
											<span className="text-lg font-semibold text-foreground-dark">Basic Information</span>
											<span className="text-base text-foreground/70">
												Ensure that all requested information is complete to facilitate proper collaboration among all legal representatives involved.
											</span>
										</div>
									</div>
									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="email"
											render={({ field }) => (
												<FormItem>
													<FormLabel required className="flex flex-row gap-1 items-center">
														Email Address
														<Tooltip
															className="z-[90]"
															content={
																<div className="flex flex-col w-full gap-1">
																	<div className="text-foreground-dark/90">Email must be valid and active as it will be used for the following:</div>
																	<div className="text-foreground-dark/70 font-medium">
																		• To automatically create an account in our system, unless it's already taken.
																	</div>
																	<div className="text-foreground-dark/70 font-medium">• To set or update an account password.</div>
																	<div className="text-foreground-dark/70 font-medium">• To receive important updates and notifications.</div>
																</div>
															}
															side="top"
														>
															<IconHelpCircle stroke={2} size={20} className="text-primary-600/90" />
														</Tooltip>
													</FormLabel>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-3 flex">
										<FormField
											control={form.control}
											name="mobile_number"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel>Mobile Number</FormLabel>
													</div>
													<FormGroup iconPosition="left">
														<FormPrepend>+639</FormPrepend>
														<Input
															{...field}
															autoComplete="off"
															placeholder=""
															maxLength={9}
															disabled={emailExists}
															onChange={(e) => {
																form.trigger("mobile_number");
																const onlyNumbers = e.target.value.replace(/\D/g, "");
																field.onChange(onlyNumbers);
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-3 flex">
										<FormField
											control={form.control}
											name="landline"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel>Landline Number</FormLabel>
													</div>
													<FormGroup iconPosition="left">
														<Input
															{...field}
															autoComplete="off"
															placeholder=""
															maxLength={9}
															disabled={emailExists}
															onChange={(e) => {
																form.trigger("landline");
																const onlyNumbers = e.target.value.replace(/\D/g, "");
																field.onChange(onlyNumbers);
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-4 flex">
										<FormField
											control={form.control}
											name="first_name"
											render={({ field }) => (
												<FormItem>
													<FormLabel required>First Name</FormLabel>
													<FormGroup iconPosition="left">
														<Input disabled={emailExists} autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-4 flex">
										<FormField
											control={form.control}
											name="middle_name"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Middle Name</FormLabel>
													<FormGroup iconPosition="left">
														<Input disabled={emailExists} autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-3 flex">
										<FormField
											control={form.control}
											name="last_name"
											render={({ field }) => (
												<FormItem>
													<FormLabel required>Last Name</FormLabel>
													<FormGroup iconPosition="left">
														<Input disabled={emailExists} autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-q flex">
										<FormField
											control={form.control}
											name="suffix"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Suffix</FormLabel>
													<FormGroup iconPosition="left">
														<Input disabled={emailExists} autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="advanced_information"
											render={({ field }) => (
												<FormItem>
													<FormItem>
														<FormGroup>
															<div className="flex flex-row w-full gap-2 items-center">
																<FormControl>
																	<Checkbox onCheckedChange={field.onChange} size="lg" />
																</FormControl>
																<FormLabel>
																	<div className="flex flex-col w-full">
																		<span className="text-base text-foreground">Additional Information</span>
																		<span className="text-sm text-muted-foreground">Include additional counsel information.</span>
																	</div>
																</FormLabel>
															</div>
														</FormGroup>
														<FormMessage />
													</FormItem>
												</FormItem>
											)}
										/>
									</div>

									{advanced === true && (
										<>
											<div className="col-span-12 flex items-center text-[14px] text-gray-500">
												<div className="flex flex-col w-full">
													<span className="text-lg font-semibold text-foreground-dark">Counsel Address</span>
													<span className="text-base text-foreground/70">Fill in the additional details of the co-counsel</span>
												</div>
											</div>
											<div className="col-span-6 flex">
												<FormField
													control={form.control}
													name="region_code"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel>Region</FormLabel>
																<FormDescription />
															</div>
															<FormGroup>
																<Combobox
																	defaultValue={field.value}
																	returnFullOption
																	onValueChange={(value) => {
																		form.setValue("province_code", "");
																		form.setValue("city_code", "");

																		if (value) {
																			const { label, value: val } = value;
																			field.onChange(val);
																			form.setValue("region", {
																				name: label,
																				code: val,
																			});
																		}
																	}}
																	placeholderValue={{
																		label: data?.region?.name,
																		value: data?.region?.code,
																	}}
																	loadOptions={async (value: string) => {
																		const items = await regionService.list({
																			"filter[search]": value,
																		});

																		return items.data.map((item) => ({
																			label: item.name,
																			value: item.code,
																		}));
																	}}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-6 flex">
												<FormField
													control={form.control}
													name="province_code"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel>Province</FormLabel>
																<FormDescription />
															</div>
															<FormGroup>
																<Combobox
																	returnFullOption
																	defaultValue={field.value}
																	disabled={!regionCode}
																	key={regionCode}
																	onValueChange={(value) => {
																		form.setValue("city_code", "");

																		if (value) {
																			field.onChange(value.value);
																			form.setValue("province", {
																				name: value.label,
																				code: value.value,
																			});
																		}
																	}}
																	placeholderValue={{
																		label: data?.province?.name,
																		value: data?.province?.code,
																	}}
																	loadOptions={async (value: string) => {
																		const items = await provinceService.list({
																			"filter[search]": value,
																			"filter[region_code]": regionCode,
																		});

																		return items.data.map((item) => ({
																			label: item.name,
																			value: item.code,
																		}));
																	}}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-6 flex">
												<FormField
													control={form.control}
													name="city_code"
													render={({ field }) => (
														<FormItem>
															<div className="flex flex-col w-full">
																<FormLabel>City</FormLabel>
																<FormDescription />
															</div>
															<FormGroup>
																<Combobox
																	defaultValue={field.value}
																	disabled={!provinceCode}
																	key={provinceCode}
																	returnFullOption
																	onValueChange={(value) => {
																		if (value) {
																			field.onChange(value.value);
																			form.setValue("city", {
																				name: value.label,
																				code: value.value,
																			});
																		}
																	}}
																	placeholderValue={{
																		label: data?.city?.name,
																		value: data?.city?.code,
																	}}
																	loadOptions={async (value: string) => {
																		const items = await cityService.list({
																			"filter[search]": value,
																			"filter[province_code]": provinceCode,
																		});

																		return items.data.map((item) => ({
																			label: item.name,
																			value: item.code,
																		}));
																	}}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-6 flex">
												<FormField
													control={form.control}
													name="barangay"
													render={({ field }) => (
														<FormItem>
															<FormLabel>Barangay</FormLabel>
															<FormGroup iconPosition="left">
																<Input autoComplete="off" {...field} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-12 flex">
												<FormField
													control={form.control}
													name="address_1"
													render={({ field }) => (
														<FormItem>
															<FormLabel>Street/Lot No./Building/Room No.</FormLabel>
															<FormGroup iconPosition="left">
																<Input autoComplete="off" {...field} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-6 flex">
												<FormField
													control={form.control}
													name="address_2"
													render={({ field }) => (
														<FormItem>
															<FormLabel>Apartment/Suite/Unit</FormLabel>
															<FormGroup iconPosition="left">
																<Input autoComplete="off" {...field} />
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="col-span-6 flex">
												<FormField
													control={form.control}
													name="zipcode"
													render={({ field }) => (
														<FormItem>
															<FormLabel>Zip Code</FormLabel>
															<FormGroup iconPosition="left">
																<NumericFormat
																	decimalScale={2}
																	thousandsGroupStyle="thousand"
																	thousandSeparator={false}
																	className={clsx(
																		"block w-full rounded-md border-gray-300 text-gray-500 focus:border-blue-400 focus:ring-blue-400 sm:text-sm",
																		"disabled:border-slate-200 disabled:bg-slate-100 disabled:shadow-none",
																	)}
																	{...field}
																	onValueChange={(values) => {
																		field.onChange(values.floatValue);
																	}}
																	maxLength={5}
																/>
															</FormGroup>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										</>
									)}
								</div>

								<div className="grid grid-cols-12 w-full py-1 gap-x-4 gap-y-3">
									<div className="col-span-12 flex items-center text-[14px] text-gray-500">
										<div className="flex flex-col w-full">
											<span className="text-lg font-semibold text-foreground-dark">Law Firm</span>
											<span className="text-base text-foreground/70">Provide the law firm information this co-counsel is affiliated with, if applicable</span>
										</div>
									</div>
									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="law_firm"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Law Firm Name</FormLabel>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>

									<div className="col-span-6 flex">
										<FormField
											disabled={isLawfirmSame}
											control={form.control}
											name="law_firm_email"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Law Firm Email</FormLabel>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>

								<div className="grid grid-cols-12 w-full py-1 gap-x-4 gap-y-3">
									<div className="col-span-12 flex items-center text-[14px] text-gray-500">
										<div className="flex flex-col w-full">
											<span className="text-lg font-semibold text-foreground-dark">Law Firm Address</span>
											<span className="text-base text-foreground/70">Fill in the additional details of the Law Firm</span>
										</div>
									</div>
									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="lawfirm_region_code"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Region</FormLabel>
													<FormDescription />
													<FormGroup>
														<Combobox
															defaultValue={field.value}
															onValueChange={(value) => {
																form.setValue("lawfirm_province_code", "");
																form.setValue("lawfirm_city_code", "");
																field.onChange(value);
															}}
															placeholderValue={{
																label: lawFirmRegion,
																value: lawFirmRegion,
															}}
															loadOptions={async (value: string) => {
																const items = await regionService.list({
																	"filter[search]": value,
																});

																return items.data.map((item) => ({
																	label: item.name,
																	value: item.code,
																}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>

									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="lawfirm_province_code"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel>Province</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Combobox
															defaultValue={field.value}
															disabled={!lawfirmRegionCode}
															key={lawfirmRegionCode}
															onValueChange={(value) => {
																form.setValue("lawfirm_city_code", "");
																field.onChange(value);
															}}
															placeholderValue={{
																label: data?.province?.name,
																value: data?.province?.code,
															}}
															loadOptions={async (value: string) => {
																const items = await provinceService.list({
																	"filter[search]": value,
																	"filter[region_code]": lawfirmRegionCode,
																});

																return items.data.map((item) => ({
																	label: item.name,
																	value: item.code,
																}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="lawfirm_city_code"
											render={({ field }) => (
												<FormItem>
													<div className="flex flex-col w-full">
														<FormLabel>City</FormLabel>
														<FormDescription />
													</div>
													<FormGroup>
														<Combobox
															defaultValue={field.value}
															disabled={!lawfirmProvinceCode}
															key={lawfirmProvinceCode}
															onValueChange={field.onChange}
															placeholderValue={{
																label: data?.city?.name,
																value: data?.city?.code,
															}}
															loadOptions={async (value: string) => {
																const items = await cityService.list({
																	"filter[search]": value,
																	"filter[province_code]": lawfirmProvinceCode,
																});

																return items.data.map((item) => ({
																	label: item.name,
																	value: item.code,
																}));
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-6 flex">
										<FormField
											control={form.control}
											name="lawfirm_barangay"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Barangay</FormLabel>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="lawfirm_address_1"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Street/Lot No./Building/Room No.</FormLabel>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-8 flex">
										<FormField
											control={form.control}
											name="lawfirm_address_2"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Apartment/Suite/Unit</FormLabel>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" {...field} />
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
									<div className="col-span-4 flex">
										<FormField
											control={form.control}
											name="lawfirm_zipcode"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Zip Code</FormLabel>
													<FormGroup iconPosition="left">
														<NumericFormat
															decimalScale={2}
															thousandsGroupStyle="thousand"
															thousandSeparator={false}
															className={clsx(
																"block w-full rounded-md border-gray-300 text-gray-500 focus:border-blue-400 focus:ring-blue-400 sm:text-sm",
																"disabled:border-slate-200 disabled:bg-slate-100 disabled:shadow-none",
															)}
															{...field}
															onValueChange={(values) => {
																field.onChange(values.floatValue);
															}}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								</div>
							</div>
						)}

						{replacementType === "rb2" && (
							<div className="flex flex-col items-center justify-between w-full py-3 px-4 gap-3">
								<div className="col-span-12 w-full py-1">
									<FormField
										control={form.control}
										name="replacement_counsel_id"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Counsel</FormLabel>
													<FormDescription />
												</div>
												<FormGroup>
													<Combobox
														defaultValue={field.value}
														returnFullOption
														onValueChange={(value) => {
															if (value) {
																form.setValue("replacement_counsel_id", value?.value);
																form.setValue("replacement_counsel", value?.model);
															}
														}}
														loadOptions={async (input: string) => {
															// Hit your /users with role=Counsel and search=<input>
															const res = await queryClient.fetchQuery(
																usersQueryOptions(
																	{
																		include: "roles",
																		search: input || undefined, // <-- drives the backend filter
																		per_page: 20, // optional: keep results lean
																	},
																	{ staleTime: 60_000 }, // cached for 1 minute
																),
															);

															const list = res?.data ?? [];

															return list
																.filter((u: any) => u.id !== user.user.id)
																.map((u: any) => ({
																	label: u.full_name,
																	value: u.id,
																	model: u,
																}));
														}}
													/>
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>

									{/* {replacementCounsel.id !== undefined && (
										<div className="flex flex-col w-full rounded-md border border-border/50 overflow-hidden">
											<div className="flex flex-col w-full">
												<table className="text-sm w-full">
													<tbody>
														<tr className="border-b last:border-b-0 border-border/50">
															<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Mobile Number</td>
															<td className="p-3 text-foreground py-2 px-3">
																{replacementCounsel.mobile_number && "+639"} {replacementCounsel.mobile_number}
															</td>
														</tr>
														<tr className="border-b last:border-b-0 border-border/50">
															<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Landline Number</td>
															<td className="p-3 text-foreground py-2 px-3">{replacementCounsel.landline}</td>
														</tr>
														{replacementCounsel.law_firm && (
															<>
																<tr className="border-b last:border-b-0 border-border/50">
																	<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Law Firm</td>
																	<td className="p-3 text-foreground py-2 px-3">{replacementCounsel.law_firm}</td>
																</tr>
																<tr className="border-b last:border-b-0 border-border/50">
																	<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Law Firm Email</td>
																	<td className="p-3 text-foreground py-2 px-3">{replacementCounsel.law_firm_email}</td>
																</tr>
																<tr className="border-b last:border-b-0 border-border/50">
																	<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Law Firm Address</td>
																	<td className="p-3 text-foreground py-2 px-3">{replacementCounsel.lawfirm_address}</td>
																</tr>
															</>
														)}
														<tr className="border-b last:border-b-0 border-border/50">
															<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Address</td>
															<td className="p-3 text-foreground py-2 px-3 group">
																<div className="flex items-center justify-between gap-2">
																	<span>{replacementCounsel.address}</span>
																</div>
															</td>
														</tr>
													</tbody>
												</table>
											</div>
										</div>
									)} */}
								</div>
							</div>
						)}
					</div>
				</div>

				<div className="flex flex-row justify-between w-full pt-4">
					<Button
						className=""
						variant="ghost"
						type="button"
						onClick={() => {
							closeModal();
						}}
					>
						Cancel
					</Button>
					<Button className="" loading={form.formState.isSubmitting} disabled={!isEdit && sameEmail && undef}>
						Confirm
					</Button>
				</div>
			</form>
		</Form>
	);
}
